import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useCallback } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useCreateCustomer } from '@/hooks/useCustomerType';
import { SubmitConfig } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import {
  generateSecurePassword,
  generateUsername,
} from '@/sections/customerManagement/components/modals/AddCustomerModal/utils';
import { createSubscriptionIfNeeded } from '@/sections/customerManagement/utils';
import { useAuth } from '@/state/auth';

/**
 * Hook for handling customer creation submission
 */
export const useCustomerSubmit = ({
  formData,
  enabledFlags,
  onSuccess,
  onClose,
}: SubmitConfig) => {
  const createCustomer = useCreateCustomer();
  const axios = useAxios();
  const { getToken } = useAuth();
  const enableRbac = useRbacEnabled();

  const handleSubmit = useCallback(async () => {
    // Generate credentials
    const username = generateUsername(formData.settings_display_name);
    const password = generateSecurePassword();

    // Create the customer
    await createCustomer.mutateAsync({
      username,
      password,
      settings_display_name: formData.settings_display_name,
      customer_type: formData.customer_type || undefined,
      profile_picture: formData.profile_picture,
      enabled_flags: enabledFlags,
      plextrac_id: formData.plextrac_id || undefined,
      collaborators:
        formData.collaborators.length > 0
          ? formData.collaborators.map(item => ({
              email: item.collaborator,
              role: enableRbac ? item.role : 'admin',
            }))
          : [],
    });

    // Create subscription if applicable
    const token = await getToken();
    await createSubscriptionIfNeeded({
      formData,
      username,
      token,
      axios,
    });

    // Trigger success callback with created customer email and close modal
    await onSuccess?.(username);
    onClose();
  }, [
    formData,
    enabledFlags,
    enableRbac,
    createCustomer,
    axios,
    getToken,
    onSuccess,
    onClose,
  ]);

  return {
    handleSubmit,
    isPending: createCustomer.isPending,
    isError: createCustomer.isError,
    error: createCustomer.error,
  };
};
